import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import {
  fetchActivities,
  fetchCostCenters,
  fetchAccounts,
  fetchSummary,
  processAllocation,
} from './activitymap.apis';

import {
  IMapActivity,
  IMapCostCenter,
  IMapAccount,
} from './activitymap.models';

import ActivityMapActivityGrid from './ActivityMapActivityGrid';
import ActivityMapAccountGrid from './ActivityMapAccountGrid';
import ActivityMapBulkDialog from './ActivityMapBulkDialog';
import ActivityMapSummaryPanel from './ActivityMapSummaryPanel';

type SelectionModel = GridRowSelectionModel | any;

function emptySelection(): GridRowSelectionModel {
  return {
    type: 'include',
    ids: new Set(),
  } as GridRowSelectionModel;
}

function selectionCount(selection: GridRowSelectionModel): number {
  return selection?.ids?.size ?? 0;
}

function selectionIds(selection: GridRowSelectionModel): string[] {
  return Array.from(selection?.ids ?? []).map(String);
}

type State = {
  loading: boolean;
  error: string;
  costcenters: IMapCostCenter[];
  selectedCostCenter: string;
  activitySearch: string;
  accountSearch: string;
  activities: IMapActivity[];
  accounts: IMapAccount[];
  selectedActivities: SelectionModel;
  selectedAccounts: SelectionModel;
  dialogOpen: boolean;
  summary: any;
};

export default class ActivityMapWorkbench extends React.Component<any, State> {
  state: State = {
    loading: false,
    error: '',
    costcenters: [],
    selectedCostCenter: '',
    activitySearch: '',
    accountSearch: '',
    activities: [],
    accounts: [],
    selectedActivities: emptySelection(),
    selectedAccounts: emptySelection(),
    dialogOpen: false,
    summary: {},
  };

  async componentDidMount() {
    await this.loadInitial();
  }

  loadInitial = async () => {
    try {
      this.setState({ loading: true, error: '' });

      const [costcenters, accounts, summary] = await Promise.all([
        fetchCostCenters(),
        fetchAccounts('', true),
        fetchSummary(),
      ]);

      const selectedCostCenter =
        Array.isArray(costcenters) && costcenters.length > 0
          ? String(costcenters[0].recid)
          : '';

      this.setState(
        {
          costcenters: Array.isArray(costcenters) ? costcenters : [],
          accounts: Array.isArray(accounts) ? accounts : [],
          selectedCostCenter,
          summary,
        },
        async () => {
          if (selectedCostCenter) {
            await this.loadActivities();
          }
        }
      );
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadActivities = async () => {
    if (!this.state.selectedCostCenter) return;

    try {
      const [activities, summary] = await Promise.all([
        fetchActivities(this.state.selectedCostCenter, this.state.activitySearch),
        fetchSummary(this.state.selectedCostCenter),
      ]);

      this.setState({
        activities: Array.isArray(activities) ? activities : [],
        summary,
        selectedActivities: emptySelection(),
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    }
  };

  loadAccounts = async () => {
    try {
      const accounts = await fetchAccounts(this.state.accountSearch, true);

      this.setState({
        accounts: Array.isArray(accounts) ? accounts : [],
        selectedAccounts: emptySelection(),
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    }
  };

  submitAllocation = async (mode: 'RATIO_ONLY' | 'EQUAL', totalAmount: number) => {
    try {
      this.setState({ loading: true, error: '', dialogOpen: false });

      await processAllocation({
        costcenterid: this.state.selectedCostCenter,
        activityids: selectionIds(this.state.selectedActivities),
        accountids: selectionIds(this.state.selectedAccounts),
        mode,
        totalamount: totalAmount,
        editorid: '0',
      });

      await Promise.all([this.loadActivities(), this.loadAccounts()]);
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };
  

  render() {
    const activityCount = selectionCount(this.state.selectedActivities);
    const accountCount = selectionCount(this.state.selectedAccounts);

    const canAllocate =
      !!this.state.selectedCostCenter &&
      activityCount > 0 &&
      accountCount > 0;

    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5">Account Mapping Engine</Typography>
            <Typography variant="body2" color="text.secondary">
              SONABHY 2025 Simulation — bulk map cost-center activities to accounts.
            </Typography>
          </Box>

          {this.state.error && <Alert severity="error">{this.state.error}</Alert>}

          <ActivityMapSummaryPanel summary={this.state.summary} />

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Cost Center</InputLabel>
                  <Select
                    label="Cost Center"
                    value={this.state.selectedCostCenter}
                    onChange={(e) =>
                      this.setState(
                        {
                          selectedCostCenter: String(e.target.value),
                          selectedActivities: emptySelection(),
                        },
                        this.loadActivities
                      )
                    }
                  >
                    {this.state.costcenters.map((cc) => (
                      <MenuItem key={cc.recid} value={String(cc.recid)}>
                        {cc.shortcode} — {cc.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  label="Search activities"
                  fullWidth
                  value={this.state.activitySearch}
                  onChange={(e) => this.setState({ activitySearch: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') this.loadActivities();
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  label="Search accounts"
                  fullWidth
                  value={this.state.accountSearch}
                  onChange={(e) => this.setState({ accountSearch: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') this.loadAccounts();
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={this.loadActivities}>
                    Refresh
                  </Button>
                  <Button variant="outlined" onClick={this.loadAccounts}>
                    Search
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Activities ({this.state.activities.length})
              </Typography>

              <ActivityMapActivityGrid
                rows={this.state.activities}
                selection={this.state.selectedActivities}
                onSelectionChange={(selectedActivities) =>
                  this.setState({ selectedActivities })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Six-digit Accounts ({this.state.accounts.length})
              </Typography>

              <ActivityMapAccountGrid
                rows={this.state.accounts}
                selection={this.state.selectedAccounts}
                onSelectionChange={(selectedAccounts) =>
                  this.setState({ selectedAccounts })
                }
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">
                Selected: {activityCount} activities × {accountCount} accounts
              </Typography>

              <Button
                variant="contained"
                disabled={!canAllocate || this.state.loading}
                onClick={() => this.setState({ dialogOpen: true })}
              >
                Allocate Selected
              </Button>
            </Stack>
          </Paper>
        </Stack>

        <ActivityMapBulkDialog
          open={this.state.dialogOpen}
          activityCount={activityCount}
          accountCount={accountCount}
          onClose={() => this.setState({ dialogOpen: false })}
          onSubmit={this.submitAllocation}
        />
      </Box>
    );
  }
}